import { Input, InputNumber, Select } from "antd";
import { AktDatePicker } from "components/aktDatePicker";
import UdfCustomField from "./UdfCustomField";

// Common styles
const fullWidthStyle = { width: "100%" };

// Individual input components for forms (simple, single-value inputs)
function TextInput({ currentValue, setCurrentValue }) {
  return (
    <Input
      style={fullWidthStyle}
      placeholder="Enter text value"
      value={currentValue || ""}
      onChange={(e) => setCurrentValue(e.target.value)}
    />
  );
}

function NumberInput({ currentValue, setCurrentValue, step = 1, placeholder = "Enter number" }) {
  return (
    <InputNumber
      style={fullWidthStyle}
      placeholder={placeholder}
      value={currentValue}
      onChange={setCurrentValue}
      step={step}
    />
  );
}

function DecimalInput({ currentValue, setCurrentValue }) {
  return (
    <InputNumber
      style={fullWidthStyle}
      placeholder="Enter amount"
      value={currentValue}
      onChange={setCurrentValue}
      step={0.01}
      precision={2}
      prefix="$"
    />
  );
}

function DateInput({ currentValue, setCurrentValue }) {
  return (
    <AktDatePicker
      style={fullWidthStyle}
      placeholder="Select date"
      value={currentValue}
      onChange={(date) => {
        setCurrentValue(date);
      }}
      format="YYYY-MM-DD"
    />
  );
}

function BooleanInput({ currentValue, setCurrentValue }) {
  return (
    <Select
      style={fullWidthStyle}
      popupMatchSelectWidth={false}
      placeholder="Select true/false"
      value={currentValue ?? null}
      onChange={(value) => {
        if (value === true || value === false) {
          setCurrentValue(value);
        }
      }}
      options={[
        { label: "True", value: true },
        { label: "False", value: false },
      ]}
    />
  );
}

function SelectInput({ currentValue, setCurrentValue, choices = [] }) {
  return (
    <Select
      style={fullWidthStyle}
      popupMatchSelectWidth={false}
      placeholder="Select an option..."
      value={currentValue ?? null}
      onChange={(value) => {
        setCurrentValue(value);
      }}
      allowClear
      options={choices.map((choice) => ({
        label: choice,
        value: choice,
      }))}
      showSearch
      filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
    />
  );
}

function MultiSelectInput({ addValue, currentValue, setCurrentValue, choices = [] }) {
  return (
    <Select
      style={fullWidthStyle}
      popupMatchSelectWidth={false}
      placeholder="Select one or more options..."
      value={currentValue ?? null}
      onChange={(value) => {
        if (value) {
          // multi_select always auto-adds to the list
          addValue(value);
          setCurrentValue(null); // Reset to controlled null state
        }
      }}
      options={choices.map((choice) => ({
        label: choice,
        value: choice,
      }))}
      showSearch
      filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
    />
  );
}

// Field type to component mapping (form components)
const FIELD_COMPONENTS = {
  text: TextInput,
  string: TextInput,
  integer: NumberInput,
  decimal: DecimalInput,
  number: NumberInput,
  date: DateInput,
  boolean: BooleanInput,
  select: SelectInput,
  multi_select: MultiSelectInput,
};

// Value adapter (no transformation for forms)
const VALUE_ADAPTER = {
  toInternal: (value) => value || {},
  fromInternal: (value) => value,
};

/**
 * Form-specific wrapper around UdfCustomField
 * - Uses simple single-value inputs (no + buttons)
 * - Single values for all field types (except multi_select)
 * - Direct value storage without transformation
 */
function UdfCustomFieldFormItem({
  customFieldsData,
  value,
  onChange,
  placeholder = "Add custom field",
  isLoading = false,
  disabled = false,
}) {
  return (
    <UdfCustomField
      customFieldsData={customFieldsData}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      isLoading={isLoading}
      disabled={disabled}
      fieldComponents={FIELD_COMPONENTS}
      allowMultipleValuesForAllFields={false}
      valueAdapter={VALUE_ADAPTER}
    />
  );
}

export { FIELD_COMPONENTS, VALUE_ADAPTER };
export default UdfCustomFieldFormItem;
